import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Eye, EyeOff, Info } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { toast } from 'sonner'

import { PageContainer } from '@/components/shared/page-container'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { signUpUser } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { AgbModal } from '@/components/auth/agb-modal'
import { Checkbox } from '@/components/ui/checkbox'

const registerSchema = z.object({
  firstName: z.string().min(1, 'Bitte gib deinen Vornamen ein.'),
  lastName: z.string().min(1, 'Bitte gib deinen Nachnamen ein.'),
  email: z.string().trim().min(1, 'Bitte gib deine E-Mail-Adresse ein.').email('Bitte gib eine gültige E-Mail-Adresse ein.'),
  phoneNumber: z.string().trim().min(1, 'Bitte gib deine Telefonnummer ein.'),
  password: z.string().min(6, 'Das Passwort muss mindestens 6 Zeichen haben.'),
  confirmPassword: z.string().min(1, 'Bitte wiederhole dein Passwort.'),
  company: z.string().optional(),
}).refine((values) => values.password === values.confirmPassword, {
  path: ['confirmPassword'],
  message: 'Die Passwörter stimmen nicht überein.',
})

export function RegisterPage() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [company, setCompany] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [agbAccepted, setAgbAccepted] = useState(false)
  const [isAgbModalOpen, setIsAgbModalOpen] = useState(false)
  const [isCompanyTooltipOpen, setIsCompanyTooltipOpen] = useState(false)

  const isRegistrationEnabled = true

  const registerMutation = useMutation({
    mutationFn: async () => {
      const parsed = registerSchema.safeParse({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phoneNumber: phoneNumber.trim(),
        password,
        confirmPassword,
        company: company.trim() || undefined,
      })

      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Ungültige Eingaben')
      }

      if (!agbAccepted) {
        throw new Error('Bitte akzeptiere die AGB.')
      }

      return signUpUser({
        ...parsed.data,
      })
    },
    onSuccess: ({ requiresEmailConfirmation }) => {
      setSuccess(true)
      setValidationError(null)
      setFirstName('')
      setLastName('')
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      setCompany('')
      setPhoneNumber('')
      setAgbAccepted(false)
      toast.success(
        requiresEmailConfirmation
          ? 'Registrierung erfolgreich. Bitte bestätige deine E-Mail-Adresse.'
          : 'Registrierung erfolgreich. Du kannst dich jetzt einloggen.',
      )
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error)
      setValidationError(errorMessage)
      setSuccess(false)
      toast.error(errorMessage)
    },
  })

  return (
    <PageContainer className="py-10 md:py-14">
      <div className="mx-auto w-full max-w-xl space-y-6">
        {!isRegistrationEnabled ? (
          <Card className="border-primary/20 bg-primary/5 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Registrierung pausiert</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-center pb-8 px-8">
              <div className="flex justify-center py-2">
                <div className="rounded-full bg-primary/10 p-4 text-primary animate-pulse">
                  <AlertCircle className="h-12 w-12" />
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Wir nehmen aktuell keine neuen Registrierungen über die Website an.
                Bitte kontaktiere uns direkt oder versuche es zu einem späteren Zeitpunkt erneut.
              </p>
              <div className="pt-4">
                <Button asChild className="w-full h-12 text-base font-semibold shadow-lg">
                  <Link to="/login">Zum Login</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : success ? (
          <Card className="border-primary/20 bg-primary/5 shadow-xl">
            <CardHeader>
              <div className="flex justify-center pb-2">
                <div className="rounded-full bg-primary/10 p-4 text-primary">
                  <CheckCircle2 className="h-12 w-12" />
                </div>
              </div>
              <CardTitle className="text-center text-2xl">Registrierung erfolgreich</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 px-8 pb-8 text-center">
              <p className="leading-relaxed text-muted-foreground">
                Dein Konto wurde erstellt. Du hast einen Aktivierungslink per E-Mail erhalten und
                musst diesen vor dem Login bestätigen.
              </p>
              <p className="text-sm text-muted-foreground">
                Bitte schau auch in deinem SPAM Ordner nach.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Zum Login</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Registrieren</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault()
                    setValidationError(null)
                    setSuccess(false)
                    registerMutation.mutate()
                  }}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-first-name">Vorname *</Label>
                      <Input
                        id="register-first-name"
                        required
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-last-name">Nachname *</Label>
                      <Input
                        id="register-last-name"
                        required
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="register-company">Anmerkung</Label>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip
                          open={isCompanyTooltipOpen}
                          onOpenChange={setIsCompanyTooltipOpen}
                        >
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label="Hinweis zu Anmerkung"
                              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              onClick={() => setIsCompanyTooltipOpen((open) => !open)}
                            >
                              <Info className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            align="start"
                            className="max-w-64 text-pretty"
                          >
                            Geworben von, Gutscheincode, Firma, sonstige Anmerkung ...
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="register-company"
                      value={company}
                      onChange={(event) => setCompany(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">E-Mail *</Label>
                    <Input
                      id="register-email"
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Telefonnummer *</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      autoComplete="tel"
                      required
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Passwort *</Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                        className="absolute inset-y-0 right-0 inline-flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        onClick={() => setShowPassword((visible) => !visible)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Passwort wiederholen *</Label>
                    <div className="relative">
                      <Input
                        id="register-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        aria-label={showConfirmPassword ? 'Passwortwiederholung verbergen' : 'Passwortwiederholung anzeigen'}
                        className="absolute inset-y-0 right-0 inline-flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        onClick={() => setShowConfirmPassword((visible) => !visible)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {(validationError || registerMutation.isError) && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        {validationError ?? getErrorMessage(registerMutation.error)}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex items-center space-x-3 bg-muted/30 p-4 rounded-lg border border-border/50">
                    <Checkbox
                      id="agb-accepted"
                      checked={agbAccepted}
                      onCheckedChange={(checked) => {
                        if (checked === true) {
                          setIsAgbModalOpen(true)
                        } else {
                          setAgbAccepted(false)
                        }
                      }}
                    />
                    <div className="flex flex-col gap-0.5 leading-none">
                      <Label
                        htmlFor="agb-accepted"
                        className="text-sm font-medium leading-none cursor-pointer hover:text-primary transition-colors"
                      >
                        Ich akzeptiere die{' '}
                        <button
                          type="button"
                          className="text-primary underline hover:text-primary/80"
                          onClick={(e) => {
                            e.preventDefault()
                            setIsAgbModalOpen(true)
                          }}
                        >
                          AGB
                        </button>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Du musst bis zum Ende der AGB scrollen, um diese zu akzeptieren.
                      </p>
                    </div>
                  </div>

                  <AgbModal
                    open={isAgbModalOpen}
                    onOpenChange={setIsAgbModalOpen}
                    onAccept={() => {
                      setAgbAccepted(true)
                      setIsAgbModalOpen(false)
                    }}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      registerMutation.isPending || 
                      !agbAccepted
                    }
                  >
                    {registerMutation.isPending ? 'Registrierung läuft ...' : 'Konto erstellen'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground">
              Du hast schon ein Konto? <Link className="text-primary underline" to="/login">Zum Login</Link>
            </p>
          </>
        )}

        {!isRegistrationEnabled && (
          <div className="text-center space-y-2">
            <p className="text-xs text-muted-foreground/60">
              Du hast schon ein Konto? <Link className="text-primary hover:underline" to="/login">Logge dich hier ein</Link>
            </p>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
